import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, chatStream, newClientToken } from "../lib/api";
import TopNav from "../components/TopNav.jsx";
import LiveCostMeter from "../components/LiveCostMeter.jsx";
import ErrorRatePill from "../components/ErrorRatePill.jsx";
import LengthDial from "../components/LengthDial.jsx";
import MachineReceipt from "../components/MachineReceipt.jsx";
import SovereignWidget from "../components/SovereignWidget.jsx";
import FirstTurnWelcome from "../components/FirstTurnWelcome.jsx";

const IDLE_MS = 5 * 60 * 1000;

export default function SagePage() {
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [depth, setDepth] = useState("normal");
  const [security, setSecurity] = useState("standard");
  const [search, setSearch] = useState(false);
  const [attachments, setAttachments] = useState([]);  // [{name, mime, data (base64)}]
  const fileInputRef = useRef(null);
  const [estimate, setEstimate] = useState(null);
  const [live, setLive] = useState(false);
  const [liveCost, setLiveCost] = useState(0);
  const [finalCost, setFinalCost] = useState(null);
  const [errRateBump, setErrRateBump] = useState(0);
  const [idle, setIdle] = useState(false);
  const [memory, setMemory] = useState(null);  // {total_prior_turns, kept_verbatim, compressed_from, window, soft_warn_active, compression_active}
  const [notePrompt, setNotePrompt] = useState(null);  // {due, days_since, last_at}
  const [noteDraftOpen, setNoteDraftOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [consensusOpen, setConsensusOpen] = useState(false);
  const [consensusPrompt, setConsensusPrompt] = useState("");
  const [consensusResult, setConsensusResult] = useState(null);
  const [consensusRunning, setConsensusRunning] = useState(false);
  const bottomRef = useRef(null);
  const idleTimer = useRef(null);
  const clientToken = useRef(newClientToken()).current;
  // Front 2 · voice-typed prompt via browser Web Speech API (no server upload)
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  // Front 6 · cycling example prompts for the six Sage markets — pinned to
  // the input placeholder so a first-time visitor sees working prompts.
  const SIX_MARKET_EXAMPLES = [
    "solo-dev — walk me through wiring Purser payments into a FastAPI app with idempotent capture.",
    "marketer — read my landing page and tell me what I sound like to a solo dev.",
    "author — critique the opening chapter of my novel for pacing and tension.",
    "artist — describe the mood arc I should use to score a 90-second product film.",
    "app-builder — help me split a 12,000-line server.py into eight cohesive routers.",
    "chatbot-seeker — what's the difference between a session id and a user id in a chat app?",
  ];
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    // Detect browser speech recognition
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) setVoiceSupported(true);
  }, []);

  useEffect(() => {
    // Cycle example prompts every 4.5 seconds while input is empty (Front 6)
    if (input.trim()) return;
    const t = setInterval(() => {
      setExampleIdx((i) => (i + 1) % SIX_MARKET_EXAMPLES.length);
    }, 4500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [input]);

  const toggleVoice = () => {
    // Front 2 · toggle browser Web Speech API. Runs on-device — no audio
    // upload to any server. Transcript populates the textarea live.
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Voice input not supported in this browser. Try Chrome or Edge.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    let baseline = input;
    rec.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput((baseline ? baseline + " " : "") + transcript.trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns]);

  // idle timer: after last activity, if 5 min go by with no send, show paused banner
  const resetIdle = () => {
    setIdle(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), IDLE_MS);
  };
  useEffect(() => {
    resetIdle();
    return () => idleTimer.current && clearTimeout(idleTimer.current);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Poll once on mount — do we owe the Ledger a Sovereign's Note this month?
    api.foundersNoteNextPrompt().then((r) => setNotePrompt(r)).catch(() => {});
  }, []);

  const submitFoundersNote = async () => {
    if (!noteTitle.trim() || !noteBody.trim()) { toast.error("Title + body required."); return; }
    try {
      await api.postFoundersNote(noteTitle.trim(), noteBody.trim(), "note");
      toast.success("Note filed. It's on the Ledger.");
      setNoteDraftOpen(false);
      setNoteTitle("");
      setNoteBody("");
      setNotePrompt({ due: false, days_since: 0 });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (!input.trim()) { setEstimate(null); return; }
    const t = setTimeout(async () => {
      try {
        const est = await api.estimate(input, depth);
        setEstimate(est);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [input, depth]);

  const send = async (overrideDepth, messageOverride) => {
    const raw = messageOverride ?? input;
    if (!raw.trim() || live) return;
    const msg = raw.trim();
    if (!messageOverride) setInput("");
    resetIdle();
    const userTurn = { role: "user", content: msg };
    const asstTurn = { role: "assistant", content: "", streaming: true };
    setTurns((t) => [...t, userTurn, asstTurn]);
    setLive(true);
    setFinalCost(null);
    setLiveCost(0);

    const effectiveDepth = overrideDepth || depth;
    let outputTokens = 0;
    const rates = { "claude-sonnet-4-5": 15 / 1_000_000, "claude-haiku-4-5": 5 / 1_000_000 };
    let modelUsed = "claude-sonnet-4-5";

    try {
      for await (const ev of chatStream({ message: msg, depth: effectiveDepth, security, session_id: sessionId, search, attachments })) {
        if (ev.event === "meta") {
          setSessionId(ev.data.session_id);
          modelUsed = ev.data.model;
          if (ev.data.memory) setMemory(ev.data.memory);
        } else if (ev.event === "token") {
          outputTokens += Math.max(1, Math.round(ev.data.text.length / 4));
          setLiveCost(outputTokens * (rates[modelUsed] ?? rates["claude-sonnet-4-5"]));
          setTurns((all) => {
            const copy = [...all];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: last.content + ev.data.text };
            return copy;
          });
        } else if (ev.event === "receipt") {
          setFinalCost(ev.data.billed_usd);
          setTurns((all) => {
            const copy = [...all];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, receipt: ev.data, streaming: false, turn_id: ev.data.turn_id };
            return copy;
          });
          setErrRateBump((n) => n + 1);
        } else if (ev.event === "error") {
          const errMsg = ev.data.message || "Sage stumbled.";
          toast.error(errMsg);
          // Close the stuck streaming turn so the page never freezes.
          setTurns((all) => {
            const copy = [...all];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              copy[copy.length - 1] = {
                ...last,
                content: (last.content || "") + `\n\n[wire cut — ${errMsg}]`,
                streaming: false,
                wire_cut: true,
              };
            }
            return copy;
          });
        }
      }
    } catch (e) {
      toast.error(e.message || "Chat failed.");
      setTurns((all) => {
        const copy = [...all];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = { ...last, content: (last.content || "") + `\n\n[wire cut — ${e.message}]`, streaming: false, wire_cut: true };
        }
        return copy;
      });
    } finally {
      setLive(false);
      setAttachments([]);
      resetIdle();
    }
  };

  const reseed = () => {
    if (live) return;
    if (!turns.length) { toast("Already a fresh session."); return; }
    if (!window.confirm("Reseed will start a fresh session anchored on your Seed doc. Current chat stays in your history. Continue?")) return;
    setSessionId(null);
    setTurns([]);
    setMemory(null);
    setFinalCost(null);
    setLiveCost(0);
    toast.success("Reseeded. Fresh session, Seed doc still holds.");
  };

  const rateReply = async (turn, label) => {
    if (!turn.turn_id || !sessionId) return;
    try {
      await api.rate(sessionId, turn.turn_id, label);
      toast.success(`Rated: ${label}`);
    } catch (e) { toast.error(e.message); }
  };

  const retract = async (turn) => {
    if (!turn.turn_id || !sessionId) return;
    const note = window.prompt("Reason for retraction:");
    if (!note) return;
    try {
      await api.retract(sessionId, turn.turn_id, note);
      toast.success("Retracted.");
      setTurns((all) => all.map((t) => t.turn_id === turn.turn_id ? { ...t, retracted: true } : t));
    } catch (e) { toast.error(e.message); }
  };

  const refundReply = async (turn) => {
    if (!turn.turn_id || !sessionId) return;
    try {
      const res = await api.refund(sessionId, turn.turn_id);
      toast.success(`Refunded $${(res.refunded_usd ?? 0).toFixed(6)}.`);
      setTurns((all) => all.map((t) => t.turn_id === turn.turn_id ? { ...t, refunded: true } : t));
    } catch (e) { toast.error(e.message); }
  };

  const handoff = async (turn) => {
    const tool = window.prompt("Which tool would be better here? (Perplexity, Cursor, ChatGPT, etc.)");
    if (!tool) return;
    const reason = window.prompt("Optional note (why is that tool better?):") || "";
    try {
      await api.handoff(sessionId, turn?.turn_id, tool, reason);
      toast.success("Handoff logged to the public Ledger.");
    } catch (e) { toast.error(e.message); }
  };

  // Speak a Sage reply aloud in Sister-Forge's voice · via The Choir · eleven_v3.
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const audioRefs = useRef({});
  const speakReply = async (turn, i) => {
    const text = (turn?.content || "").trim();
    if (!text) return;
    if (speakingIdx === i && audioRefs.current[i]) {
      // stop the currently-playing audio
      try { audioRefs.current[i].pause(); } catch (_) {}
      audioRefs.current[i] = null;
      setSpeakingIdx(null);
      return;
    }
    try {
      setSpeakingIdx(i);
      const res = await api.speak(text.slice(0, 2000));
      const b64 = res?.audio_base64;
      if (!b64) throw new Error("Choir returned no audio.");
      const audio = new Audio(`data:audio/mpeg;base64,${b64}`);
      audioRefs.current[i] = audio;
      audio.onended = () => { audioRefs.current[i] = null; setSpeakingIdx((cur) => (cur === i ? null : cur)); };
      audio.onerror = () => { audioRefs.current[i] = null; setSpeakingIdx((cur) => (cur === i ? null : cur)); toast.error("Audio playback failed."); };
      await audio.play();
    } catch (e) {
      setSpeakingIdx(null);
      toast.error(e.message || "Voicework failed.");
    }
  };

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";  // reset input so same file can be re-picked
    for (const f of files) {
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`${f.name}: too large (20MB max).`);
        continue;
      }
      const data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1] || "");
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      setAttachments((all) => [...all, { name: f.name, mime: f.type || "application/octet-stream", data, size: f.size }]);
      toast.success(`Attached: ${f.name}`);
    }
  };

  const removeAttachment = (i) => setAttachments((all) => all.filter((_, j) => j !== i));

  const exportAll = async () => {
    try {
      const res = await api.export();
      const blob = new Blob([res.markdown], { type: "text/markdown" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `sage-export-${Date.now()}.md`;
      a.click();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-sage-pale text-sage-text sage-grain relative">
      <TopNav />

      <div className="border-b border-sage-border bg-sage-elevated">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <LiveCostMeter live={live} estimate={liveCost} final={finalCost} />
            <ErrorRatePill refreshKey={errRateBump} />
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.15em] text-sage-muted">
            <button
              onClick={reseed}
              disabled={live}
              data-testid="chat-reseed"
              title="Start a fresh session anchored on your Seed doc"
              className="border border-sage-border px-3 py-1 hover:text-sage-accent hover:border-sage-accent transition-colors duration-150 disabled:opacity-40"
            >
              ↻ Reseed
            </button>
            <button
              onClick={() => setConsensusOpen(true)}
              disabled={live}
              data-testid="chat-consensus"
              title="Ask the same question of Claude + OpenAI + Gemini and see the dissent"
              className="border border-sage-border px-3 py-1 hover:text-sage-accent hover:border-sage-accent transition-colors duration-150 disabled:opacity-40"
            >
              ⚖ Consensus
            </button>
            <button
              onClick={exportAll}
              data-testid="chat-export"
              className="border border-sage-border px-3 py-1 hover:text-sage-accent hover:border-sage-accent transition-colors duration-150"
            >
              ↓ Export .md
            </button>
            <div className="border border-sage-border px-3 py-1 bg-sage-bg">
              Security:{" "}
              <select
                value={security}
                onChange={(e) => setSecurity(e.target.value)}
                data-testid="security-select"
                className="bg-transparent text-sage-accent focus:outline-none"
              >
                <option value="standard">standard</option>
                <option value="private">private</option>
                <option value="research">research</option>
                <option value="refuge">refuge · whistleblower</option>
              </select>
            </div>
            <button
              onClick={() => setSearch(!search)}
              data-testid="search-toggle"
              className={`border px-3 py-1 uppercase tracking-[0.15em] transition-colors duration-150 ${
                search
                  ? "border-sage-accent text-sage-accent bg-sage-pink"
                  : "border-sage-border text-sage-muted hover:text-sage-accent hover:border-sage-accent"
              }`}
            >
              Web {search ? "ON" : "OFF"}
            </button>
          </div>
        </div>
      </div>

      <SovereignWidget memory={memory} />

      {notePrompt?.due && !noteDraftOpen && (
        <div className="border-b border-sage-warn bg-sage-elevated" data-testid="founders-note-nudge">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-2 flex items-center justify-between gap-4 text-xs">
            <span className="text-sage-warn uppercase tracking-[0.15em]">
              Founder's Note due · {notePrompt.days_since ?? "∞"}d since the last entry
            </span>
            <div className="flex gap-3">
              <button
                data-testid="founders-note-dismiss"
                onClick={() => setNotePrompt({ due: false })}
                className="text-sage-muted uppercase tracking-[0.15em] hover:text-sage-text"
              >
                later
              </button>
              <button
                data-testid="founders-note-write"
                onClick={() => setNoteDraftOpen(true)}
                className="border border-sage-warn text-sage-warn px-3 py-1 uppercase tracking-[0.15em] hover:bg-sage-warn hover:text-sage-bg"
              >
                Write a note
              </button>
            </div>
          </div>
        </div>
      )}

      {noteDraftOpen && (
        <div className="border-b border-sage-accent bg-sage-elevated" data-testid="founders-note-drawer">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-3 flex flex-col gap-2">
            <input
              data-testid="founders-note-title"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder="Title · e.g. Day 30 · what we learned"
              className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:border-sage-accent"
            />
            <textarea
              data-testid="founders-note-body"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="One paragraph. Signed by you. Goes straight to the Ledger."
              rows={4}
              className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:border-sage-accent resize-y sage-prose"
            />
            <div className="flex justify-end gap-2">
              <button
                data-testid="founders-note-cancel"
                onClick={() => { setNoteDraftOpen(false); setNoteTitle(""); setNoteBody(""); }}
                className="border border-sage-border text-sage-muted px-3 py-1 text-xs uppercase tracking-[0.15em] hover:text-sage-text"
              >
                Cancel
              </button>
              <button
                data-testid="founders-note-submit"
                onClick={submitFoundersNote}
                className="border border-sage-accent text-sage-accent px-3 py-1 text-xs uppercase tracking-[0.15em] hover:bg-sage-accent hover:text-sage-bg"
              >
                File on the Ledger
              </button>
            </div>
          </div>
        </div>
      )}

      {idle && !live && turns.length > 0 && (
        <div
          data-testid="idle-banner"
          className="border-b border-sage-border bg-sage-pink"
        >
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-2 text-xs text-sage-muted uppercase tracking-[0.15em] flex items-center justify-between">
            <span>Session paused · Sage is spending nothing while you're away.</span>
            <button
              onClick={resetIdle}
              data-testid="idle-resume"
              className="text-sage-accent hover:underline"
            >
              Resume →
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
        {turns.length === 0 && (
          <>
            <FirstTurnWelcome />
            <div className="border border-sage-border p-8 text-sage-muted text-sm bg-sage-elevated mt-4" data-testid="chat-empty">
              <div className="text-xs uppercase tracking-[0.2em] text-sage-faint mb-3">Session — new</div>
              Ask anything. Sage will keep it as short as the answer allows. When it can't verify a fact, it will say so. When it disagrees, it will say so — once, plainly.
            </div>
          </>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`mb-6 ${t.retracted ? "opacity-50" : ""}`} data-testid={`turn-${i}`}>
            {t.role === "user" ? (
              <div className="border-l-2 border-sage-accent pl-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">You</div>
                <div className={`text-sage-text whitespace-pre-wrap ${t.retracted ? "line-through" : ""}`}>{t.content}</div>
              </div>
            ) : (
              <div className="border border-sage-border p-5 bg-sage-elevated">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2 flex justify-between">
                  <span>Sage</span>
                  {t.retracted && <span className="text-sage-warn">RETRACTED</span>}
                  {t.refunded && !t.retracted && <span className="text-sage-accent">REFUNDED</span>}
                  {t.wire_cut && !t.retracted && !t.refunded && (
                    <span data-testid={`wire-cut-${i}`} className="text-sage-warn">WIRE CUT</span>
                  )}
                </div>
                <div className={`text-sage-text whitespace-pre-wrap leading-relaxed ${t.retracted ? "line-through" : ""}`}>
                  {t.content}
                  {t.streaming && <span className="sage-cursor" />}
                </div>
                {t.receipt && (
                  <MachineReceipt
                    receipt={t.receipt}
                    refunded={t.refunded}
                    onRefund={!t.retracted ? () => refundReply(t) : null}
                  />
                )}
                {t.receipt && !t.retracted && (
                  <div className="mt-3 flex gap-2 text-[10px] uppercase tracking-[0.1em] flex-wrap">
                    {/* Front 1 · regen chips — same prompt, different depth */}
                    {i > 0 && turns[i - 1]?.role === "user" && (
                      <>
                        <button
                          onClick={() => {
                            const prev = turns[i - 1]?.content;
                            if (prev) send("tight", prev);
                          }}
                          data-testid={`regen-shorter-${i}`}
                          disabled={live}
                          className="border border-sage-border px-3 py-1 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150 disabled:opacity-40"
                          title="Regenerate this reply at half length"
                        >
                          ↧ Shorter
                        </button>
                        <button
                          onClick={() => {
                            const prev = turns[i - 1]?.content;
                            if (prev) send("thorough", prev);
                          }}
                          data-testid={`regen-deeper-${i}`}
                          disabled={live}
                          className="border border-sage-border px-3 py-1 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150 disabled:opacity-40"
                          title="Regenerate this reply at double depth"
                        >
                          ↥ Deeper
                        </button>
                      </>
                    )}
                    {["honest", "slop", "wrong"].map((lbl) => (
                      <button
                        key={lbl}
                        onClick={() => rateReply(t, lbl)}
                        data-testid={`rate-${lbl}-${i}`}
                        className="border border-sage-border px-3 py-1 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150"
                      >
                        {lbl}
                      </button>
                    ))}
                    <button
                      onClick={() => retract(t)}
                      data-testid={`retract-${i}`}
                      className="border border-sage-border px-3 py-1 hover:border-sage-warn hover:text-sage-warn transition-colors duration-150 ml-auto"
                    >
                      Retract
                    </button>
                    <button
                      onClick={() => speakReply(t, i)}
                      data-testid={`speak-${i}`}
                      className="border border-sage-border px-3 py-1 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150"
                      title="Hear this reply in Sister-Forge's voice"
                    >
                      {speakingIdx === i ? "◼ Stop" : "▶ Speak"}
                    </button>
                    <button
                      onClick={() => handoff(t)}
                      data-testid={`handoff-${i}`}
                      className="border border-sage-border px-3 py-1 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150"
                    >
                      Handoff →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-0 border-t border-sage-border bg-sage-elevated">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-4 flex flex-col gap-3">
          <LengthDial value={depth} onChange={setDepth} estimate={estimate} />
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="attachment-chips">
              {attachments.map((a, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 border border-sage-border bg-sage-elevated px-3 py-1 text-xs text-sage-muted"
                >
                  <span className="text-sage-accent">◈</span>
                  <span className="truncate max-w-xs">{a.name}</span>
                  <span className="text-sage-faint">
                    ({(a.size / 1024).toFixed(0)}kb · {a.mime.split("/").pop()})
                  </span>
                  <button
                    onClick={() => removeAttachment(i)}
                    data-testid={`attach-remove-${i}`}
                    className="text-sage-faint hover:text-sage-err transition-colors duration-150"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={onFilePick}
              className="hidden"
              data-testid="chat-file-input"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.json,.csv,.html,.js,.py,.tsx,.jsx"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={live}
              data-testid="chat-attach"
              title="Attach files (PDF, image, or text)"
              className="border border-sage-border text-sage-muted px-4 py-3 hover:border-sage-accent hover:text-sage-accent transition-colors duration-150 disabled:opacity-40 text-xs uppercase tracking-[0.1em]"
            >
              Attach
            </button>
            {voiceSupported && (
              <button
                onClick={toggleVoice}
                disabled={live}
                data-testid="chat-voice"
                title={listening ? "Stop recording (voice input)" : "Start voice input (Chrome/Edge)"}
                className={`border px-4 py-3 transition-colors duration-150 disabled:opacity-40 text-xs uppercase tracking-[0.1em] ${
                  listening
                    ? "border-red-500 text-red-500 animate-pulse"
                    : "border-sage-border text-sage-muted hover:border-sage-accent hover:text-sage-accent"
                }`}
              >
                {listening ? "● Rec" : "🎙 Voice"}
              </button>
            )}
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={resetIdle}
              placeholder={input ? "Ask Sage anything…" : SIX_MARKET_EXAMPLES[exampleIdx]}
              rows={2}
              data-testid="chat-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              className="flex-1 bg-sage-elevated border border-sage-border p-3 text-sage-text placeholder:text-sage-faint focus:border-sage-accent transition-colors duration-150 resize-none"
            />
            <button
              onClick={() => send()}
              disabled={live || !input.trim()}
              data-testid="chat-send"
              className="border border-sage-accent text-sage-accent px-6 py-3 uppercase tracking-[0.1em] text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40 whitespace-nowrap"
            >
              {live ? "…" : "Send ⌘↵"}
            </button>
          </div>
        </div>
      </div>

      {consensusOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={() => !consensusRunning && setConsensusOpen(false)}
          data-testid="consensus-modal"
        >
          <div
            className="bg-sage-bg border border-sage-border max-w-4xl w-full p-6 mt-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-baseline mb-4 border-b border-sage-border pb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Cross-Model Consensus</div>
                <div className="text-lg font-bold uppercase tracking-tight text-sage-text mt-1">
                  Claude + OpenAI + Gemini · same prompt · public dissent
                </div>
              </div>
              <button
                onClick={() => setConsensusOpen(false)}
                disabled={consensusRunning}
                data-testid="consensus-close"
                className="text-sage-muted hover:text-sage-text text-xl leading-none disabled:opacity-40"
              >×</button>
            </div>

            <textarea
              value={consensusPrompt}
              onChange={(e) => setConsensusPrompt(e.target.value)}
              data-testid="consensus-prompt"
              placeholder="Ask a question. Sage answers by polling three model families in parallel."
              rows={3}
              disabled={consensusRunning}
              className="w-full border border-sage-border bg-sage-bg text-sage-text px-3 py-2 text-sm mb-3 disabled:opacity-40"
            />

            <div className="flex justify-end gap-2 mb-4">
              <button
                onClick={async () => {
                  if (!consensusPrompt.trim()) { toast.error("Give me a prompt."); return; }
                  setConsensusRunning(true);
                  setConsensusResult(null);
                  try {
                    const r = await api.consensus(consensusPrompt);
                    setConsensusResult(r);
                    toast.success(`Agreement ${r.agreement_score} · ${r.dissent ? "DISSENT" : "agree"} · ${r.wall_clock_ms}ms`);
                  } catch (e) {
                    toast.error(`Consensus failed: ${e.message}`);
                  } finally {
                    setConsensusRunning(false);
                  }
                }}
                disabled={consensusRunning || !consensusPrompt.trim()}
                data-testid="consensus-run"
                className="border border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 uppercase tracking-[0.1em] text-xs hover:opacity-90 disabled:opacity-40"
              >
                {consensusRunning ? "Polling…" : "Poll all three ↗"}
              </button>
            </div>

            {consensusResult && (
              <div className="space-y-3" data-testid="consensus-result">
                <div className="border border-sage-border p-3 text-xs flex justify-between items-baseline flex-wrap gap-2">
                  <div className="text-sage-muted">
                    Agreement: <span className={`font-bold tabular-nums ${consensusResult.dissent ? "text-sage-warn" : "text-sage-accent"}`}>{consensusResult.agreement_score}</span>
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.15em] ${consensusResult.dissent ? "text-sage-warn" : "text-sage-accent"}`}>
                    {consensusResult.dissent ? "DISSENT · they disagree" : "AGREE · all three converged"}
                  </div>
                  <div className="text-sage-faint tabular-nums">
                    Wall clock {consensusResult.wall_clock_ms}ms · Filed on Ledger
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {consensusResult.calls.map((c, i) => (
                    <div key={i} className="border border-sage-border p-3 text-xs" data-testid={`consensus-answer-${c.provider}`}>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted mb-1">{c.provider}</div>
                      <div className="text-sage-text font-mono text-[10px] mb-2 truncate">{c.model}</div>
                      <div className="text-sage-faint text-[10px] mb-2 tabular-nums">{c.latency_ms}ms</div>
                      {c.error ? (
                        <div className="text-sage-err whitespace-pre-wrap">Error: {c.error}</div>
                      ) : (
                        <div className="text-sage-muted whitespace-pre-wrap sage-prose max-h-64 overflow-y-auto">{c.answer}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[10px] text-sage-faint mt-4 border-t border-sage-border pt-3">
              Every consensus run is logged to <span className="text-sage-accent">/sage-ledger</span>. When the three disagree, the world sees the disagreement.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
